import React, { useEffect, useState, type ReactNode } from 'react';
import { signalRService } from '../../lib/signalr';
import { useAuthContext } from '../Auth/useAuthContext';
import { SignalRContext, type SignalRContextType } from './context';
import { useLogger } from '@/lib/logger';
import type {
  SignalREventName,
  SignalREventHandlers,
} from '../../lib/signalr/types';

interface SignalRProviderProps {
  children: ReactNode;
}

export const SignalRProvider: React.FC<SignalRProviderProps> = ({
  children,
}) => {
  const logger = useLogger();
  const [connectionState, setConnectionState] = useState(
    signalRService.getConnectionState()
  );
  const authContext = useAuthContext();
  const isAuthenticated = authContext?.isAuthenticated ?? false;
  const isLoading = authContext?.isLoading ?? true;

  // Update connection state periodically
  useEffect(() => {
    const interval = setInterval(() => {
      setConnectionState(signalRService.getConnectionState());
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // Auto-connect when user is authenticated
  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      signalRService.startConnection().catch((error) => {
        logger.error('Failed to start SignalR connection', {
          error: error instanceof Error ? error.message : String(error),
        });
      });
    } else if (!isAuthenticated) {
      signalRService.stopConnection().catch((error) => {
        logger.error('Failed to stop SignalR connection', {
          error: error instanceof Error ? error.message : String(error),
        });
      });
    }
  }, [isAuthenticated, isLoading]);

  const startConnection = async (): Promise<void> => {
    await signalRService.startConnection();
    setConnectionState(signalRService.getConnectionState());
  };

  const stopConnection = async (): Promise<void> => {
    await signalRService.stopConnection();
    setConnectionState(signalRService.getConnectionState());
  };

  const reconnect = async (): Promise<void> => {
    await signalRService.reconnect();
    setConnectionState(signalRService.getConnectionState());
  };

  const on = <T extends SignalREventName>(
    eventName: T,
    handler: SignalREventHandlers[T]
  ): void => {
    signalRService.on(eventName, handler);
  };

  const off = <T extends SignalREventName>(
    eventName: T,
    handler: SignalREventHandlers[T]
  ): void => {
    signalRService.off(eventName, handler);
  };

  const value: SignalRContextType = {
    connectionState,
    startConnection,
    stopConnection,
    reconnect,
    on,
    off,
  };

  return (
    <SignalRContext.Provider value={value}>{children}</SignalRContext.Provider>
  );
};
